import { z } from "zod";

import {
	isValidEmail,
	isValidLocationInHeaderView,
	isValidPhoneNumber,
	isValidURL,
} from "../../handlers/validationFunctions";

/**
 * Shared, reusable Zod field builders for the resume builder.
 *
 * Defined once here and composed by the per-section schemas so email / phone /
 * URL / location / date rules are consistent across every page instead of
 * being re-hand-rolled. Message strings default to the legacy copy.
 */

export const requiredString = (message = "This field is required") => z.string().trim().min(1, message);

/**
 * A failed `safeParse` reduced to one message per field, keyed by field name.
 *
 * Every builder page reports its problems on the field that caused them rather
 * than in a banner above the form, and each field slot takes a single string —
 * so only the first issue per field is kept.
 */
export function firstFieldErrors(error: z.ZodError): Record<string, string> {
	const messages: Record<string, string> = {};
	for (const issue of error.issues) {
		const field = String(issue.path[0] ?? "");
		if (field && messages[field] === undefined) messages[field] = issue.message;
	}
	return messages;
}

export const emailField = (message = "Please enter a valid email address") =>
	requiredString("Email is required").refine(isValidEmail, message);

export const phoneField = (message = "Please enter a valid phone number eg: 255712XXXXXX") =>
	requiredString("Phone number is required").refine(isValidPhoneNumber, message);

/** Empty allowed (row not started); when present it must be a valid URL. */
export const optionalUrlField = (message = "Enter a valid URL (https://...)") =>
	z
		.string()
		.trim()
		.refine((value) => value === "" || isValidURL(value), message);

export const tzLocationField = (message = "Please enter a valid address eg District, Region") =>
	requiredString("Address is required").refine(isValidLocationInHeaderView, message);

/**
 * "City, Region" typed into one box, where either part on its own is enough.
 *
 * Only the emptiness rule lives here. `isValidLocationInHeaderView` rejects odd
 * punctuation but accepts strings that carry no location at all — "," and " "
 * both pass it — so the one guarantee callers need is that something real was
 * typed. A caller that stores the parts separately is responsible for filling
 * the gap when just one is given.
 */
export const cityOrRegionField = (message = "Please enter a city or region") =>
	requiredString("Address is required")
		.refine(isValidLocationInHeaderView, message)
		.refine((value) => value.split(",").some((part) => part.trim() !== ""), message);

// --- dates -------------------------------------------------------------------

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** True for a real calendar date in "YYYY-MM-DD" form (rejects 2026-02-31, 2026-99-99). */
export function isRealCalendarDate(value: string): boolean {
	if (!YMD.test(value)) return false;
	const [year, month, day] = value.split("-").map(Number);
	const date = new Date(year, month - 1, day);
	return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/** A `type=date` value ("YYYY-MM-DD"); empty allowed unless composed as required. */
export const isoDateString = (message = "Enter a valid date") =>
	z.string().refine((value) => value === "" || isRealCalendarDate(value), message);

/** True when `dateStr` is empty or not after today (a worked date can't be in the future). */
export function isNotInFuture(dateStr: string): boolean {
	if (!dateStr) return true;
	const parsed = new Date(dateStr);
	if (Number.isNaN(parsed.getTime())) return false;
	const endOfToday = new Date();
	endOfToday.setHours(23, 59, 59, 999);
	return parsed.getTime() <= endOfToday.getTime();
}

/** True when `start <= end`. An empty `end` (current employer) is treated as open. */
export function isStartOnOrBeforeEnd(start: string, end: string): boolean {
	if (!start || !end) return true;
	return new Date(start).getTime() <= new Date(end).getTime();
}

/** Graduation year: allow future (expected graduation) but within a sane window. */
export function isPlausibleGraduationYear(year: string | number): boolean {
	const value = Number(year);
	if (!Number.isInteger(value)) return false;
	const currentYear = new Date().getFullYear();
	return value >= 1950 && value <= currentYear + 8;
}

/** Date of birth: optional, but when present must be in the past with a plausible age. */
export function isPlausibleDateOfBirth(dateStr: string): boolean {
	if (!dateStr) return true;
	if (!isRealCalendarDate(dateStr)) return false;
	const parsed = new Date(dateStr);
	const now = new Date();
	if (parsed.getTime() >= now.getTime()) return false;
	const ageYears = (now.getTime() - parsed.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
	return ageYears >= 14 && ageYears <= 100;
}

export const dateOfBirthField = (message = "Enter a valid date of birth") =>
	z.string().refine(isPlausibleDateOfBirth, message);
